import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ArrowRight,
  User,
  Mail,
  Lock,
  Shield,
  Calendar,
  Save,
  Eye,
  EyeOff,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Profile({ user, setUser }) {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Profile form
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/profile`);
      setProfile(response.data);
      setFullName(response.data.full_name || '');
      setEmail(response.data.email || '');
    } catch (error) {
      toast.error('فشل تحميل الملف الشخصي');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    
    try {
      const response = await axios.put(`${API}/profile`, {
        full_name: fullName,
        email: email,
      });
      
      toast.success(response.data.message);
      setProfile(response.data.user);
      
      // Update user in parent component
      if (setUser) {
        setUser(prev => ({
          ...prev,
          full_name: fullName,
          email: email,
        }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل تحديث الملف الشخصي');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('كلمة المرور الجديدة غير متطابقة');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('كلمة المرور يجب أن تكون 6 أحرف على الأقل');
      return;
    }
    
    setChangingPassword(true);
    
    try {
      const response = await axios.post(`${API}/profile/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
      });
      
      toast.success(response.data.message);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل تغيير كلمة المرور');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleBack = () => {
    if (user?.role === 'legal_manager') {
      navigate('/companies');
    } else {
      navigate('/executive');
    }
  };

  const getRoleName = (role) => {
    return role === 'legal_manager' ? 'مدير الشؤون القانونية' : 'الرئيس التنفيذي';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-8" dir="rtl">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={handleBack}
            className="mb-4"
            data-testid="back-button"
          >
            <ArrowRight size={20} className="ml-2" />
            العودة
          </Button>
          <h1 className="text-4xl font-bold text-slate-900 mb-2 flex items-center gap-3">
            <User className="text-[#E94B7B]" size={40} />
            الملف الشخصي
          </h1>
          <p className="text-slate-600">إدارة معلومات حسابك وتغيير كلمة المرور</p>
        </div>

        <div className="space-y-6">
          {/* Profile Info Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-right flex items-center gap-2">
                <User className="text-blue-600" size={24} />
                معلومات الحساب
              </CardTitle>
              <CardDescription className="text-right">
                تعديل الاسم والبريد الإلكتروني
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUpdateProfile} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="fullName" className="text-right block">الاسم الكامل</Label>
                    <div className="relative">
                      <User className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                      <Input
                        id="fullName"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        className="pr-10"
                        placeholder="أدخل اسمك"
                        data-testid="profile-name-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-right block">البريد الإلكتروني</Label>
                    <div className="relative">
                      <Mail className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                      <Input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pr-10 text-left"
                        dir="ltr"
                        placeholder="example@ifg.com"
                        data-testid="profile-email-input"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Read-only info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                  <div className="space-y-2">
                    <Label className="text-right block text-slate-500">الصلاحية</Label>
                    <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                      <Shield className="text-[#E94B7B]" size={18} />
                      <span className="font-medium">{getRoleName(profile?.role)}</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-right block text-slate-500">تاريخ التسجيل</Label>
                    <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                      <Calendar className="text-slate-500" size={18} />
                      <span>
                        {profile?.created_at 
                          ? new Date(profile.created_at).toLocaleDateString('ar-SA')
                          : '-'}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end pt-4">
                  <Button
                    type="submit"
                    disabled={savingProfile}
                    className="bg-[#E94B7B] hover:bg-[#d43a6a]"
                    data-testid="save-profile-button"
                  >
                    <Save size={18} className="ml-2" />
                    {savingProfile ? 'جارٍ الحفظ...' : 'حفظ التغييرات'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Change Password Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-right flex items-center gap-2">
                <Lock className="text-amber-600" size={24} />
                تغيير كلمة المرور
              </CardTitle>
              <CardDescription className="text-right">
                تأكد من استخدام كلمة مرور قوية
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword" className="text-right block">كلمة المرور الحالية</Label>
                  <div className="relative">
                    <Lock className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <Input
                      id="currentPassword"
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="pr-10 pl-10"
                      placeholder="أدخل كلمة المرور الحالية"
                      required
                      data-testid="current-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="newPassword" className="text-right block">كلمة المرور الجديدة</Label>
                    <div className="relative">
                      <Lock className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                      <Input
                        id="newPassword"
                        type={showNewPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="pr-10 pl-10"
                        placeholder="أدخل كلمة المرور الجديدة"
                        required
                        data-testid="new-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className="text-right block">تأكيد كلمة المرور</Label>
                    <div className="relative">
                      <Lock className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                      <Input
                        id="confirmPassword"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="pr-10"
                        placeholder="أعد كتابة كلمة المرور"
                        required
                        data-testid="confirm-password-input"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="text-sm text-slate-500">
                  💡 كلمة المرور يجب أن تكون 6 أحرف على الأقل
                </div>
                
                <div className="flex justify-end pt-4">
                  <Button
                    type="submit"
                    disabled={changingPassword}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="change-password-button"
                  >
                    <Lock size={18} className="ml-2" />
                    {changingPassword ? 'جارٍ التغيير...' : 'تغيير كلمة المرور'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
